import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

export default function HeroSection() {
  const featuredArticles = articles.filter((a) => a.featured).slice(0, 4);
  const [current, setCurrent] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % featuredArticles.length);
    }, 5000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [featuredArticles.length]);

  const article = featuredArticles[current];

  return (
    <section className="relative h-[500px] md:h-[620px] w-full overflow-hidden">
      {featuredArticles.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={item.coverImage}
            alt={item.title}
            className="w-full h-full object-cover"
            title={`${item.title} - 首页推荐`}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55"></div>

      <div className="absolute inset-0 flex items-center">
        <div className="max-w-7xl mx-auto px-4 md:px-6 w-full">
          <div className="max-w-2xl">
            <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-primary-500 text-white mb-4 animate-[fadeInUp_0.6s_ease-out]">
              今日推荐
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 leading-tight animate-[fadeInUp_0.6s_ease-out_0.1s]">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-white/80 line-clamp-2 mb-6 animate-[fadeInUp_0.6s_ease-out_0.2s]">
              {article.excerpt}
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 animate-[fadeInUp_0.6s_ease-out_0.3s]">
              <Link
                to={`/article/${article.id}`}
                className="inline-flex items-center px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                阅读全文
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <div className="flex items-center space-x-5 text-white/70 text-sm">
                <span className="flex items-center">
                  <i className="ri-star-fill text-accent-400 mr-1"></i>
                  {article.rating}
                </span>
                <span className="flex items-center">
                  <i className="ri-eye-line mr-1"></i>
                  {article.viewCount.toLocaleString()} 次阅读
                </span>
                <span>{article.publishDate}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center space-x-2">
        {featuredArticles.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrent(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              index === current ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/70'
            }`}
            aria-label={`切换到第 ${index + 1} 张`}
          />
        ))}
      </div>
    </section>
  );
}